/**
 * Report Upload Form Component
 * Demonstrates how to use the report upload API
 */

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Upload, FileText, CheckCircle, AlertCircle } from 'lucide-react';
import { useReportUpload } from '@/hooks/useReportUpload';
import { ReportUploadData } from '@/integrations/supabase/reportUploadService';

export function ReportUploadForm() {
  const [formData, setFormData] = useState<Partial<ReportUploadData>>({
    legacy_id: '',
    id_group: '',
    period: '',
    report_date: '',
    id_team: '',
    report_name: '',
    summary: '',
    created_by_user_id: '',
    bod_comment: '',
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] = useState<any>(null);

  const { uploadReport, isUploading, uploadProgress, error, reset } = useReportUpload();

  const handleInputChange = (field: keyof ReportUploadData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setSelectedFile(file || null);
    setUploadResult(null);
    reset();
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!selectedFile) {
      return;
    }

    try {
      const result = await uploadReport({
        ...formData,
        file: selectedFile,
      } as ReportUploadData);

      setUploadResult(result);
    } catch (err) {
      console.error('Upload failed:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      legacy_id: '',
      id_group: '',
      period: '',
      report_date: '',
      id_team: '',
      report_name: '',
      summary: '',
      created_by_user_id: '',
      bod_comment: '',
    });
    setSelectedFile(null);
    setUploadResult(null);
    reset();
  };

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload className="h-5 w-5" />
          Upload Báo Cáo
        </CardTitle>
        <CardDescription>
          Demo API upload file báo cáo lên Supabase Storage và lưu thông tin vào database
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Required Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="legacy_id">Legacy ID (Optional)</Label>
              <Input
                id="legacy_id"
                value={formData.legacy_id || ''}
                onChange={(e) => handleInputChange('legacy_id', e.target.value)}
                placeholder="Nhập Legacy ID (có thể để trống)"
              />
            </div>
            
            <div>
              <Label htmlFor="period">Kỳ báo cáo</Label>
              <Input
                id="period"
                value={formData.period || ''}
                onChange={(e) => handleInputChange('period', e.target.value)}
                placeholder="VD: W2501"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="id_group">ID Group</Label>
              <Input
                id="id_group"
                value={formData.id_group || ''}
                onChange={(e) => handleInputChange('id_group', e.target.value)}
                placeholder="Nhập ID Group"
              />
            </div>
            
            <div>
              <Label htmlFor="id_team">ID Team</Label>
              <Input
                id="id_team"
                value={formData.id_team || ''}
                onChange={(e) => handleInputChange('id_team', e.target.value)}
                placeholder="Nhập ID Team"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="report_name">Tên báo cáo</Label>
            <Input
              id="report_name"
              value={formData.report_name || ''}
              onChange={(e) => handleInputChange('report_name', e.target.value)}
              placeholder="Nhập tên báo cáo"
            />
          </div>

          <div>
            <Label htmlFor="report_date">Ngày báo cáo</Label>
            <Input
              id="report_date"
              type="date"
              value={formData.report_date || ''}
              onChange={(e) => handleInputChange('report_date', e.target.value)}
            />
          </div>

          <div>
            <Label htmlFor="summary">Tóm tắt</Label>
            <Textarea
              id="summary"
              value={formData.summary || ''}
              onChange={(e) => handleInputChange('summary', e.target.value)}
              placeholder="Nhập tóm tắt báo cáo"
              rows={3}
            />
          </div>

          <div>
            <Label htmlFor="bod_comment">Nhận xét BOD</Label>
            <Textarea
              id="bod_comment"
              value={formData.bod_comment || ''}
              onChange={(e) => handleInputChange('bod_comment', e.target.value)}
              placeholder="Nhập nhận xét của BOD"
              rows={2}
            />
          </div>

          <div>
            <Label htmlFor="created_by_user_id">Người tạo (User ID)</Label>
            <Input
              id="created_by_user_id"
              value={formData.created_by_user_id || ''}
              onChange={(e) => handleInputChange('created_by_user_id', e.target.value)}
              placeholder="Nhập ID người tạo"
            />
          </div>

          {/* File Upload */}
          <div>
            <Label htmlFor="file">File báo cáo *</Label>
            <Input
              id="file"
              type="file"
              onChange={handleFileChange}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
              required
            />
            {selectedFile && (
              <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                <FileText className="h-4 w-4" />
                {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
              </div>
            )}
          </div>

          {/* Upload Progress */}
          {isUploading && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Đang upload...</span>
                <span>{uploadProgress}%</span>
              </div>
              <Progress value={uploadProgress} className="w-full" />
            </div>
          )}

          {/* Error Display */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Success Result */}
          {uploadResult && uploadResult.success && (
            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>
                <div className="space-y-1">
                  <div><strong>Upload thành công!</strong></div>
                  <div>Report ID: {uploadResult.data?.report_id}</div>
                  <div>File Path: {uploadResult.data?.file_path}</div>
                  <div>
                    File URL: 
                    <a 
                      href={uploadResult.data?.file_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="ml-1 text-blue-600 hover:underline"
                    >
                      Xem file
                    </a>
                  </div>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 pt-4">
            <Button 
              type="submit" 
              disabled={!selectedFile || isUploading}
              className="flex-1"
            >
              {isUploading ? 'Đang upload...' : 'Upload Báo Cáo'}
            </Button>
            
            <Button 
              type="button" 
              variant="outline" 
              onClick={resetForm}
              disabled={isUploading}
            >
              Reset
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
